import {
  faBellExclamation,
  faCalendarDay,
  faCalendarXmark,
  faCircleCheck,
  faCircleXmark,
  faClockRotateLeft,
  faDotCircle,
  faEnvelopeDot,
  faHourglassStart,
  faPaperPlane,
  faWrench
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Chip } from "@mui/material"
import theme from "css/theme/light"
import { FC } from "react"
import { ChipInfoIconProps, ChipInfoStatusEnum, CustomChipInfoProps } from "./chipInfo.interface"
import { styled } from "@mui/material/styles"
import { faCircle } from "@fortawesome/pro-light-svg-icons"
import { useTranslation } from "react-i18next"

interface CustomChipProps {
  borderColor?: string
  backgroundColor?: string
  textColor?: string
}

const CustomChip = styled(Chip, {
  shouldForwardProp: (prop) =>
    prop !== "borderColor" && prop !== "backgroundColor" && prop !== "textColor",
})<CustomChipProps>(({ borderColor, backgroundColor, textColor }) => ({
  borderRadius: "4px",
  fontWeight: 500,
  "& .MuiChip-label": {
    paddingRight: "8px !important",
  },
  ...(borderColor && { border: `1px solid ${borderColor}` }),
  ...(backgroundColor && { backgroundColor }),
  ...(textColor && { color: textColor }),
}))


const StyledFontAwesomeIcon = styled(FontAwesomeIcon)({
  marginLeft: '8px !important',
})

const ChipInfoIcon: FC<ChipInfoIconProps> = ({ type, iconSize, custom }) => {
  const { t } = useTranslation()
  const { icon, label, color } = custom
    ? custom
    : ({ icon: faDotCircle, label: "", color: "primary" } as CustomChipInfoProps)

  switch (type) {
    case ChipInfoStatusEnum.OnGoing:
      return (
        <CustomChip
          borderColor={(theme.palette as any).sky[700]}
          backgroundColor={(theme.palette as any).sky[100]}
          textColor={(theme.palette as any).sky[700]}
          variant="outlined"
          color={"primary"}
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faCalendarDay} color={(theme.palette as any).sky[700]} />}
          label={t("global.ongoing", "Ongoing")}
        />
      )
    case ChipInfoStatusEnum.None:
      return (
        <CustomChip
          borderColor={(theme.palette as any).default[600]}
          backgroundColor={(theme.palette as any).default[100]}
          textColor={(theme.palette as any).default[600]}
          color="info"
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faCircle} color={(theme.palette as any).default[600]} />}
          label={label ? label : t("learning.titles.not_started", "Not started")}
        />
      )
    case ChipInfoStatusEnum.New:
      return (
        <CustomChip
          borderColor={(theme.palette as any).default[800]}
          textColor={(theme.palette as any).default[800]}
          color={"info"}
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faEnvelopeDot} color={(theme.palette as any).warning[500]} />}
          label={label ? label : t("global.new", "New") + "!"}
        />
      )
    case ChipInfoStatusEnum.Planned:
      return (
        <CustomChip
          borderColor={(theme.palette as any).sky[700]}
          backgroundColor={(theme.palette as any).sky[100]}
          textColor={(theme.palette as any).sky[700]}
          color={"primary"}
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faClockRotateLeft} color={(theme.palette as any).sky[700]} />}
          label={t("global.planned", "Planned")}
        />
      )
    case ChipInfoStatusEnum.Assigned:
      return (
        <CustomChip
          borderColor={(theme.palette as any).purple[600]}
          backgroundColor={(theme.palette as any).purple[100]}
          textColor={(theme.palette as any).purple[600]}
          color={"primary"}
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faPaperPlane} color={(theme.palette as any).purple[600]} />}
          label={t("global.assigned", "Assigned")}
        />
      )
    case ChipInfoStatusEnum.Sent:
      return (
        <CustomChip
          borderColor={(theme.palette as any).purple[600]}
          backgroundColor={(theme.palette as any).purple[100]}
          textColor={(theme.palette as any).purple[600]}
          color={"primary"}
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faPaperPlane} color={(theme.palette as any).purple[600]} />}
          label={t("global.sent", "Sent")}
        />
      )
    case ChipInfoStatusEnum.Booked:
      return (
        <CustomChip
          borderColor={(theme.palette as any).sky[700]}
          backgroundColor={(theme.palette as any).sky[100]}
          textColor={(theme.palette as any).sky[700]}
          color={"primary"}
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faCalendarDay} color={(theme.palette as any).sky[700]} />}
          label={t("global.ongoing", "Ongoing")}
        />
      )
    case ChipInfoStatusEnum.Resolved:
      return (
        <CustomChip
          borderColor={(theme.palette as any).yellow[800]}
          backgroundColor={(theme.palette as any).yellow[100]}
          textColor={(theme.palette as any).yellow[800]}
          color={"primary"}
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faWrench} color={(theme.palette as any).yellow[800]} />}
          label={t("global.fixed", "Fixed")}
        />
      )
    case ChipInfoStatusEnum.Completed:
      return (
        <CustomChip
          borderColor={(theme.palette as any).success[700]}
          backgroundColor={(theme.palette as any).green[200]}
          textColor={(theme.palette as any).success[700]}
          color={"success"}
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faCircleCheck} color={(theme.palette as any).success[700]} />}
          label={t("global.completed", "Completed")}
        />
      )
    case ChipInfoStatusEnum.Declined:
      return (
        <CustomChip
          borderColor={theme.palette.error.main}
          backgroundColor={theme.palette.error.light}
          textColor={theme.palette.error.main}
          color={"error"}
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faCalendarXmark} color={theme.palette.error.main} />}
          label={t("global.cancelled", "Cancelled")}
        />
      )
    case ChipInfoStatusEnum.Active:
      return (
        <CustomChip
          borderColor={(theme.palette as any).warning[600]}
          backgroundColor={(theme.palette as any).warning[100]}
          textColor={(theme.palette as any).warning[600]}
          color={"warning"}
          variant="outlined"
          icon={
            <StyledFontAwesomeIcon size={"1x"} icon={faBellExclamation} color={(theme.palette as any).warning[600]} />
          }
          label={t("global.active", "Active")}
        />
      )
    case ChipInfoStatusEnum.Passed:
      return (
        <CustomChip
          color="success"
          variant="outlined"
          borderColor={(theme.palette as any).success[700]}
          backgroundColor={(theme.palette as any).green[200]}
          textColor={(theme.palette as any).success[700]}
          icon={
            <StyledFontAwesomeIcon
              size="1x"
              icon={faCircleCheck}
              color={(theme.palette as any).success[700]}
            />
          }
          label={t("learning.titles.passed", "Passed")}
        />
      )
    case ChipInfoStatusEnum.NotPassed:
      return (
        <CustomChip
          color="error"
          variant="outlined"
          borderColor={theme.palette.error.main}
          backgroundColor={theme.palette.error.light}
          textColor={theme.palette.error.main}
          icon={
            <StyledFontAwesomeIcon
              size="1x"
              icon={faCircleXmark}
              color={theme.palette.error.main}
            />
          }
          label={t("learning.titles.failed", "Failed")}
        />
      )
    case ChipInfoStatusEnum.NotStarted:
      return (
        <CustomChip
          color="info"
          variant="outlined"
          borderColor={(theme.palette as any).info[900]}
          textColor={(theme.palette as any).info[900]}
          icon={
            <StyledFontAwesomeIcon
              size="1x"
              icon={faHourglassStart}
              color={(theme.palette as any).info[900]}
            />
          }
          label={t("learning.titles.not_started", "Not started")}
        />
      )
    case ChipInfoStatusEnum.Unmanaged:
      return (
        <CustomChip
          color={"warning"}
          variant="outlined"
          icon={<StyledFontAwesomeIcon size={"1x"} icon={faHourglassStart} color={theme.palette["warning"].main} />}
          label={t("global.unmanaged", "Unmanaged")}
        />
      )
    default:
      return (
        <CustomChip
          color={color ? color : "primary"}
          variant="outlined"
          icon={
            <StyledFontAwesomeIcon
              size={iconSize !== null ? iconSize : "1x"}
              icon={icon !== undefined ? icon : faHourglassStart}
              color={theme.palette[color ? color : "primary"].main}
            />
          }
          label={label ? label : t("global.status_missing", "Status missing")}
        />
      )
  }
}

export default ChipInfoIcon
